import { useEffect, useMemo, useState } from "react";
import { fetchCatalog, fetchContentPage } from "../api/catalog";
import StoreBoot from "../components/StoreBoot";
import StoreCredit from "../components/StoreCredit";
import StoreFooterLinks from "../components/StoreFooterLinks";
import StoreHeader from "../components/StoreHeader";
import StorePixels from "../components/StorePixels";
import { applyPageSeo, currentPageUrl, publicTitle, seoFromContent } from "../lib/pageSeo";
import { applyStoreTheme, readCachedStoreTheme, storeThemeFromSettings } from "../lib/storeTheme";
import { HomeSection, pageColumns } from "./HomePage";

function pageStyle(theme) {
  const settings = theme || {};
  const primary = settings.primaryColor || "";
  const gradientStart = settings.primaryGradientStart;
  const gradientEnd = settings.primaryGradientEnd;
  return {
    backgroundColor: settings.backgroundColor || "#ffffff",
    color: settings.textColor || "#111827",
    "--home-primary": primary || "#111827",
    "--home-link": settings.linkColor || primary || "inherit",
    "--home-button-bg":
      gradientStart && gradientEnd
        ? `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`
        : settings.buttonColor || primary || "#111827",
    "--home-button-text": settings.buttonTextColor || "#ffffff",
    "--home-muted": settings.mutedColor || "#6b7280",
    fontFamily:
      settings.bodyFont === "inter" ? "Inter, system-ui, sans-serif" : undefined,
    "--home-heading-font":
      settings.headingFont === "fraunces"
        ? 'Fraunces, "Cormorant Garamond", Georgia, serif'
        : settings.headingFont === "sora"
          ? "Sora, Inter, system-ui, sans-serif"
          : "Inter, system-ui, sans-serif",
  };
}

export default function ContentPage({ storeSlug, pageSlug }) {
  const preview = useMemo(
    () => new URLSearchParams(window.location.search).get("preview") === "1",
    []
  );
  const [data, setData] = useState(null);
  const [latestProducts, setLatestProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.all([
      fetchContentPage(storeSlug, pageSlug, {
        preview,
        signal: controller.signal,
      }),
      fetchCatalog(storeSlug, {
        page: 1,
        limit: 6,
        preview,
        signal: controller.signal,
      }).catch((catalogError) => {
        if (catalogError.name === "AbortError") throw catalogError;
        return { products: [] };
      }),
    ])
      .then(([pageData, catalog]) => {
        setData(pageData);
        setLatestProducts(catalog?.products || []);
        try {
          const seo = seoFromContent(pageData?.page?.content, {
            storeName: pageData?.store?.name,
            fallbackImage: pageData?.store?.logoUrl || "",
          });
          applyPageSeo({
            title: publicTitle(seo, pageData?.store?.name),
            description: seo.description,
            image: seo.image,
            url: currentPageUrl(),
            index: !preview,
          });
        } catch {
          document.title = pageData?.page?.name || pageData?.store?.name || "Loja";
        }
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError(
          requestError.status === 404
            ? "Esta página não foi encontrada."
            : "Não foi possível carregar a página."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [pageSlug, preview, storeSlug]);

  useEffect(() => {
    const font = data?.theme?.headingFont;
    const fonts = {
      fraunces: {
        id: "home-font-catalog",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500;1,9..144,600&family=Inter:wght@400;500;600;700&display=swap",
      },
      sora: {
        id: "home-font-ritual",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500;1,600&family=Sora:wght@300;400;500;600;700&family=Inter:wght@400;500;600;700&display=swap",
      },
    };
    const linkFont = fonts[font] || {
      id: "home-font-inter",
      href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
    };
    if (document.getElementById(linkFont.id)) return;
    const link = document.createElement("link");
    link.id = linkFont.id;
    link.rel = "stylesheet";
    link.href = linkFont.href;
    document.head.appendChild(link);
  }, [data?.theme?.headingFont]);

  useEffect(() => {
    if (!data?.page) return undefined;
    const settings = data.page.content?.settings || data.theme || {};
    applyStoreTheme(
      storeThemeFromSettings({
        backgroundColor: settings.backgroundColor || "#ffffff",
        textColor: settings.textColor || "#111827",
        primaryColor: settings.primaryColor || "#111827",
        linkColor: settings.linkColor || settings.primaryColor || "#111827",
        buttonColor: settings.buttonColor || settings.primaryColor || "#111827",
        buttonTextColor: settings.buttonTextColor || "#ffffff",
        mutedColor: settings.mutedColor || "#6b7280",
      })
    );
    return () => {
      const cached = readCachedStoreTheme(storeSlug);
      if (cached) applyStoreTheme(cached);
    };
  }, [data, storeSlug]);

  if (loading && !data) return <StoreBoot />;

  const theme = data?.theme || {};
  const chrome = data?.chrome || {};
  const style = pageStyle(theme);
  const sections = pageColumns(data?.page?.content).main;

  return (
    <div
      className="storefront"
      style={{ backgroundColor: style.backgroundColor, color: style.color }}
    >
      <StorePixels store={data?.store} />
      <StoreHeader
        store={data?.store}
        storeSlug={storeSlug}
        navLinks={chrome.navLinks}
        cta={chrome.cta}
        plain
      />
      <main
        className={`store-home store-home--content store-home--${theme.maxWidth || "standard"}`}
        style={style}
      >
        {error ? (
          <section className="state-card" role="alert">
            <h1>Não foi possível abrir a página</h1>
            <p>{error}</p>
          </section>
        ) : (
          <div className="store-home__layout store-home__layout--full">
            <div className="store-home__column store-home__column--main">
              {sections.map((section) => (
                <HomeSection
                  section={section}
                  latestProducts={latestProducts}
                  storeSlug={storeSlug}
                  preview={preview}
                  key={section.id}
                />
              ))}
            </div>
          </div>
        )}
      </main>
      <footer className="store-footer">
        {chrome.newsletter?.title ? (
          <form
            className="store-newsletter"
            action={chrome.newsletter.url || undefined}
            method="get"
          >
            <p>{chrome.newsletter.title}</p>
            <div>
              <input
                type="email"
                name="email"
                required
                placeholder={chrome.newsletter.placeholder || "Email"}
                disabled={!chrome.newsletter.url}
              />
              <button type="submit" disabled={!chrome.newsletter.url}>
                {chrome.newsletter.button || "OK"}
              </button>
            </div>
          </form>
        ) : null}
        {chrome.footerText ? <p>{chrome.footerText}</p> : null}
        <StoreFooterLinks links={chrome.footerLinks} storeSlug={storeSlug} />
        <StoreCredit />
      </footer>
    </div>
  );
}
